import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  Grid,
  Chip,
  Divider,
  Drawer,
} from '@mui/material';
import { Add, Refresh, Search, Edit, Delete, Campaign as CampaignIcon, Group, TrendingUp, MonetizationOn, Close } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import crmPlusService, { Campaign, CreateCampaignInput, SourceRoiRow } from '../../services/crmPlusService';

const STATUS_TONE: Record<string, StatusTone> = {
  ACTIVE: 'success',
  PLANNED: 'info',
  PAUSED: 'warning',
  COMPLETED: 'neutral',
  CANCELLED: 'error',
};

const CAMPAIGN_STATUSES = ['PLANNED', 'ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELLED'];
const CAMPAIGN_TYPES = ['Email', 'Webinar', 'Trade Show', 'Social', 'Content', 'Paid Search', 'Referral', 'Other'];

interface CampaignFormData {
  name: string;
  type: string;
  channel: string;
  status: string;
  startDate: string;
  endDate: string;
  budget: string;
  targetLeads: string;
  description: string;
}

const emptyForm: CampaignFormData = {
  name: '',
  type: '',
  channel: '',
  status: 'PLANNED',
  startDate: '',
  endDate: '',
  budget: '',
  targetLeads: '',
  description: '',
};

const money = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : formatIndianNumber(v);

const RoiCell: React.FC<{ pct: number | null }> = ({ pct }) => {
  if (pct === null || pct === undefined) {
    return <Typography variant="body2" color="text.disabled">—</Typography>;
  }
  const tone = pct >= 0 ? 'success.main' : 'error.main';
  return (
    <Typography variant="body2" sx={{ fontWeight: 700, color: tone, fontVariantNumeric: 'tabular-nums' }}>
      {pct >= 0 ? '+' : ''}
      {pct.toFixed(1)}%
    </Typography>
  );
};

const CampaignsPage: React.FC = () => {
  const chartTokens = useChartTokens();

  const [tab, setTab] = useState(0);

  // Campaigns tab state
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // Source ROI tab state
  const [sourceRoi, setSourceRoi] = useState<SourceRoiRow[]>([]);
  const [roiLoading, setRoiLoading] = useState(true);
  const [roiError, setRoiError] = useState<string | null>(null);

  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [form, setForm] = useState<CampaignFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Campaign | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [detailTarget, setDetailTarget] = useState<Campaign | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await crmPlusService.getCampaigns({
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setCampaigns(result.campaigns || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  const fetchSourceRoi = useCallback(async () => {
    setRoiLoading(true);
    setRoiError(null);
    try {
      const result = await crmPlusService.getSourceRoi();
      const rows = Array.isArray(result) ? result : result?.sources || [];
      setSourceRoi(rows);
    } catch (err: any) {
      setRoiError(err.message || 'Failed to load source ROI');
    } finally {
      setRoiLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    if (tab === 1 && sourceRoi.length === 0 && !roiLoading) {
      fetchSourceRoi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const kpis = useMemo(() => {
    const totalCampaigns = campaigns.length;
    const totalLeads = campaigns.reduce((sum, c) => sum + (c.leadsGenerated || 0), 0);
    const totalPipeline = campaigns.reduce((sum, c) => sum + (c.pipelineValue || 0), 0);
    const totalWon = campaigns.reduce((sum, c) => sum + (c.wonValue || 0), 0);
    return { totalCampaigns, totalLeads, totalPipeline, totalWon };
  }, [campaigns]);

  const roiChartData = useMemo(
    () =>
      sourceRoi.map((r) => ({
        source: r.source || 'Unknown',
        wonValue: r.wonValue || 0,
      })),
    [sourceRoi]
  );

  const openCreateDialog = () => {
    setEditingCampaign(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (e: React.MouseEvent, campaign: Campaign) => {
    e.stopPropagation();
    setEditingCampaign(campaign);
    setForm({
      name: campaign.name,
      type: campaign.type || '',
      channel: campaign.channel || '',
      status: campaign.status || 'PLANNED',
      startDate: campaign.startDate ? campaign.startDate.slice(0, 10) : '',
      endDate: campaign.endDate ? campaign.endDate.slice(0, 10) : '',
      budget: campaign.budget !== null && campaign.budget !== undefined ? String(campaign.budget) : '',
      targetLeads: campaign.targetLeads !== null && campaign.targetLeads !== undefined ? String(campaign.targetLeads) : '',
      description: campaign.description || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: CreateCampaignInput = {
        name: form.name,
        type: form.type || undefined,
        channel: form.channel || undefined,
        status: form.status || undefined,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        budget: form.budget.trim() ? parseFloat(form.budget) : null,
        targetLeads: form.targetLeads.trim() ? parseInt(form.targetLeads, 10) : null,
        description: form.description || undefined,
      };
      if (editingCampaign) {
        await crmPlusService.updateCampaign(editingCampaign.id, payload);
        setToast({ message: 'Campaign updated', severity: 'success' });
      } else {
        await crmPlusService.createCampaign(payload);
        setToast({ message: 'Campaign created', severity: 'success' });
      }
      setDialogOpen(false);
      fetchCampaigns();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to save campaign', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await crmPlusService.deleteCampaign(deleteTarget.id);
      setToast({ message: 'Campaign deleted', severity: 'success' });
      setDeleteTarget(null);
      fetchCampaigns();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to delete campaign', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const openDetail = async (campaign: Campaign) => {
    setDetailTarget(campaign);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const result = await crmPlusService.getCampaign(campaign.id);
      setDetail(result?.campaign || result);
    } catch (err: any) {
      setDetailError(err.message || 'Failed to load campaign detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="Campaigns"
        subtitle="Marketing campaigns and lead-source ROI"
        actions={
          <>
            <IconButton onClick={() => (tab === 0 ? fetchCampaigns() : fetchSourceRoi())} disabled={tab === 0 ? loading : roiLoading}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={openCreateDialog}>
              New Campaign
            </Button>
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Total Campaigns" value={kpis.totalCampaigns} icon={<CampaignIcon />} color="#7c3aed" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Leads Generated" value={kpis.totalLeads} icon={<Group />} color="#0284c7" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Pipeline Value" value={money(kpis.totalPipeline)} icon={<TrendingUp />} color="#059669" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Won Value" value={money(kpis.totalWon)} icon={<MonetizationOn />} color="#dc2626" />
        </Grid>
      </Grid>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}
      >
        <Tab label="Campaigns" />
        <Tab label="Source ROI" />
      </Tabs>

      {tab === 0 && (
        <>
          <FilterBar>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {CAMPAIGN_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Search campaigns"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FilterSpacer />
            {(statusFilter || search) && (
              <Button size="small" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </FilterBar>

          {error ? (
            <TableSurface>
              <ErrorState message={error} onRetry={fetchCampaigns} />
            </TableSurface>
          ) : (
            <TableSurface>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Name</TableCell>
                      <TableCell sx={headerCellSx}>Type / Channel</TableCell>
                      <TableCell sx={headerCellSx}>Status</TableCell>
                      <TableCell sx={headerCellSx} align="right">Budget</TableCell>
                      <TableCell sx={headerCellSx} align="right">Leads</TableCell>
                      <TableCell sx={headerCellSx} align="right">Converted</TableCell>
                      <TableCell sx={headerCellSx} align="right">Pipeline</TableCell>
                      <TableCell sx={headerCellSx} align="right">Won</TableCell>
                      <TableCell sx={headerCellSx} align="right">Cost/Lead</TableCell>
                      <TableCell sx={headerCellSx} align="right">ROI %</TableCell>
                      <TableCell sx={headerCellSx} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 6, border: 0 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : campaigns.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ border: 0 }}>
                          <TableEmptyState title="No campaigns match these filters" hint="Clear the filters, or create a new campaign." />
                        </TableCell>
                      </TableRow>
                    ) : (
                      campaigns.map((c) => (
                        <TableRow key={c.id} sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => openDetail(c)}>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {c.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {c.ownerName || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                              {c.type ? <TypePill label={c.type} tint="#0284c7" /> : <Typography variant="body2" color="text.disabled">—</Typography>}
                              {c.channel && (
                                <Typography variant="caption" color="text.secondary">
                                  {c.channel}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <StatusDot label={c.status} tone={STATUS_TONE[c.status] || 'neutral'} />
                          </TableCell>
                          <TableCell align="right">{money(c.budget)}</TableCell>
                          <TableCell align="right">{c.leadsGenerated}</TableCell>
                          <TableCell align="right">{c.convertedLeads}</TableCell>
                          <TableCell align="right">{money(c.pipelineValue)}</TableCell>
                          <TableCell align="right">{money(c.wonValue)}</TableCell>
                          <TableCell align="right">{money(c.costPerLead)}</TableCell>
                          <TableCell align="right">
                            <RoiCell pct={c.roiPct} />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={(e) => openEditDialog(e, c)} aria-label={`Edit ${c.name}`}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(c);
                              }}
                              aria-label={`Delete ${c.name}`}
                            >
                              <Delete fontSize="small" color="error" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          {roiError ? (
            <TableSurface>
              <ErrorState message={roiError} onRetry={fetchSourceRoi} />
            </TableSurface>
          ) : (
            <>
              <Box
                sx={{
                  p: 2.5,
                  mb: 2.5,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                  Won Value by Lead Source
                </Typography>
                {roiLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : roiChartData.length === 0 ? (
                  <TableEmptyState title="No source data yet" hint="Won value by lead source will appear here once leads convert." />
                ) : (
                  <Box sx={{ width: '100%', height: 320 }}>
                    <ResponsiveContainer>
                      <BarChart data={roiChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                        <CartesianGrid {...gridProps(chartTokens)} />
                        <XAxis dataKey="source" {...axisProps(chartTokens)} />
                        <YAxis {...axisProps(chartTokens)} tickFormatter={(v) => formatIndianNumber(v)} width={70} />
                        <Tooltip {...tooltipStyle(chartTokens)} formatter={(v: number) => formatIndianNumber(v)} />
                        <Bar dataKey="wonValue" name="Won Value" fill={chartTokens.categorical[0]} radius={[4, 4, 0, 0]} maxBarSize={56} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>

              <TableSurface>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Source</TableCell>
                        <TableCell sx={headerCellSx} align="right">Leads</TableCell>
                        <TableCell sx={headerCellSx} align="right">Qualified</TableCell>
                        <TableCell sx={headerCellSx} align="right">Converted</TableCell>
                        <TableCell sx={headerCellSx} align="right">Conversion %</TableCell>
                        <TableCell sx={headerCellSx} align="right">Pipeline</TableCell>
                        <TableCell sx={headerCellSx} align="right">Won</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roiLoading ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                            <CircularProgress size={28} />
                          </TableCell>
                        </TableRow>
                      ) : sourceRoi.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ border: 0 }}>
                            <TableEmptyState title="No lead-source data" hint="Source ROI appears once leads are recorded with a source." />
                          </TableCell>
                        </TableRow>
                      ) : (
                        sourceRoi.map((r) => (
                          <TableRow key={r.source} sx={bodyRowSx}>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {r.source || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{r.leadCount}</TableCell>
                            <TableCell align="right">{r.qualifiedCount}</TableCell>
                            <TableCell align="right">{r.convertedCount}</TableCell>
                            <TableCell align="right">
                              {r.conversionRatePct === null || r.conversionRatePct === undefined ? '—' : `${r.conversionRatePct.toFixed(1)}%`}
                            </TableCell>
                            <TableCell align="right">{money(r.pipelineValue)}</TableCell>
                            <TableCell align="right">{money(r.wonValue)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableSurface>
            </>
          )}
        </>
      )}

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Campaign Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                fullWidth
                label="Type"
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {CAMPAIGN_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Channel"
                value={form.channel}
                onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
              />
            </Box>
            <TextField
              select
              fullWidth
              label="Status"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              {CAMPAIGN_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Budget"
                type="number"
                value={form.budget}
                onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
              <TextField
                fullWidth
                label="Target Leads"
                type="number"
                value={form.targetLeads}
                onChange={(e) => setForm((f) => ({ ...f, targetLeads: e.target.value.replace(/[^0-9]/g, '') }))}
              />
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : editingCampaign ? 'Save Changes' : 'Create Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign detail drawer — leads generated by this campaign */}
      <Drawer anchor="right" open={!!detailTarget} onClose={() => setDetailTarget(null)}>
        <Box sx={{ width: { xs: '100vw', sm: 440 }, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {detailTarget?.name}
              </Typography>
              {detailTarget && (
                <Box sx={{ mt: 0.5 }}>
                  <StatusDot label={detailTarget.status} tone={STATUS_TONE[detailTarget.status] || 'neutral'} />
                </Box>
              )}
            </Box>
            <IconButton size="small" onClick={() => setDetailTarget(null)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : detailError ? (
            <ErrorState message={detailError} onRetry={() => detailTarget && openDetail(detailTarget)} />
          ) : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">Pipeline Value</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{money(detailTarget?.pipelineValue)}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">Won Value</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{money(detailTarget?.wonValue)}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">Leads Generated</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{detailTarget?.leadsGenerated ?? '—'}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="text.secondary">Converted</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{detailTarget?.convertedLeads ?? '—'}</Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Leads
              </Typography>
              {!detail?.leads || detail.leads.length === 0 ? (
                <TableEmptyState title="No leads yet" hint="Leads attributed to this campaign will appear here." />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {detail.leads.map((lead: any) => (
                    <Box
                      key={lead.id}
                      sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {lead.company_name || lead.companyName || 'Unnamed lead'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {lead.contact_person || lead.contactPerson || '—'}
                        </Typography>
                      </Box>
                      {lead.status && <Chip label={lead.status} size="small" variant="outlined" />}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default CampaignsPage;
